import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { sortRecommended } from '@/features/sossial/utils/connectionSuggestionFilters';
import { connectionsRepository } from '../repositories/supabaseConnectionsRepository';

export interface ConnectionSuggestion {
  id: string;
  full_name: string;
  profile_picture_url?: string;
  username?: string;
  academy_name?: string;
  academy_city?: string;
  academy_state?: string;
  country?: string | null;
  state?: string | null;
  city?: string | null;
  belt_rank?: string;
  main_academy_id?: string | null;
  profession?: string | null;
  hobby_ids?: number[];
  mutual_count?: number;
  is_premium?: boolean;
  updated_at?: string;
}

const PROFILE_LIMIT = 60;
const RESULT_LIMIT = 20;

function buildNeighborMap(
  connections: { requester: string; recipient: string }[],
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const { requester, recipient } of connections) {
    if (!map.has(requester)) map.set(requester, new Set());
    if (!map.has(recipient)) map.set(recipient, new Set());
    map.get(requester)!.add(recipient);
    map.get(recipient)!.add(requester);
  }
  return map;
}

function countMutuals(
  candidateId: string,
  myConnectionIds: Set<string>,
  neighborMap: Map<string, Set<string>>,
): number {
  const candidateNeighbors = neighborMap.get(candidateId);
  if (!candidateNeighbors) return 0;
  let count = 0;
  for (const id of myConnectionIds) {
    if (candidateNeighbors.has(id)) count++;
  }
  return count;
}

export const useConnectionSuggestions = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['connection-suggestions', user?.id],
    queryFn: async (): Promise<ConnectionSuggestion[]> => {
      if (!user) return [];

      try {
        const [
          allProfiles,
          existingConnections,
          myConnectionsRows,
          acceptedConnections,
        ] = await Promise.all([
          connectionsRepository.getProfiles(user.id, PROFILE_LIMIT),
          connectionsRepository.getConnectionsByUser(user.id),
          connectionsRepository.getMyConnections(),
          connectionsRepository.getAcceptedConnections(),
        ]);

        const connectedUserIds = new Set<string>();
        existingConnections?.forEach(conn => {
          if (conn.requester === user.id) {
            connectedUserIds.add(conn.recipient);
          } else if (conn.recipient === user.id) {
            connectedUserIds.add(conn.requester);
          }
        });

        const myConnectionIds = new Set<string>(
          (myConnectionsRows ?? [])
            .map((r: { user_id: string }) => r.user_id)
            .filter(Boolean),
        );

        const neighborMap = buildNeighborMap(acceptedConnections ?? []);

        const availableProfiles =
          allProfiles?.filter(profile => !connectedUserIds.has(profile.id)) ?? [];

        const candidateIds = availableProfiles.map(p => p.id);

        let hobbiesByProfile = new Map<string, number[]>();
        if (candidateIds.length > 0) {
          try {
            const hobbyRows = await connectionsRepository.getHobbiesByProfiles(candidateIds);
            hobbiesByProfile = hobbyRows.reduce((map, row) => {
              const list = map.get(row.profile_id) ?? [];
              list.push(row.hobby_id);
              map.set(row.profile_id, list);
              return map;
            }, new Map<string, number[]>());
          } catch {
            console.error('Error fetching profile hobbies');
          }
        }

        const profilesWithAcademies = await Promise.all(
          availableProfiles.map(async profile => {
            let academyInfo: Partial<ConnectionSuggestion> = {};

            if (profile.main_academy_id) {
              const academy = await connectionsRepository.getAcademy(profile.main_academy_id);
              if (academy) {
                academyInfo = {
                  academy_name: academy.name,
                  academy_city: academy.city,
                  academy_state: academy.state,
                };
              }
            }

            return {
              id: profile.id,
              full_name: profile.full_name || 'Anonymous',
              profile_picture_url: profile.profile_picture_url,
              username: profile.username,
              country: profile.country,
              state: profile.state,
              city: profile.city,
              main_academy_id: profile.main_academy_id ?? null,
              profession: profile.profession ?? null,
              updated_at: profile.updated_at,
              hobby_ids: hobbiesByProfile.get(profile.id) ?? [],
              mutual_count: countMutuals(profile.id, myConnectionIds, neighborMap),
              is_premium: profile.is_premium || false,
              ...academyInfo,
              belt_rank: profile.belt_rank || 'white',
            } as ConnectionSuggestion;
          }),
        );

        return sortRecommended(profilesWithAcademies).slice(0, RESULT_LIMIT);
      } catch (error) {
        console.error('Error in connection suggestions:', error);
        throw error;
      }
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });
};
